import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, ArrowLeft, ChevronRight, Quote, TrendingUp, Target, Clock, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { API } from "@/lib/constants";
import PageMeta from "@/components/PageMeta";

const DEFAULT_IMAGE = "https://customer-assets.emergentagent.com/job_e227742f-fa2d-44b4-a974-4045826f8a41/artifacts/epvouypm_DMN%20Logo%20Final%20A%20%28Light%29%20transparent.PNG";

const CaseStudyDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [caseStudy, setCaseStudy] = useState(null);
  const [relatedStudies, setRelatedStudies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCaseStudy = async () => {
      try {
        const response = await axios.get(`${API}/case-studies/${slug}`);
        setCaseStudy(response.data);
        document.title = response.data.title + " | Digital Media Ninja";
        
        const allStudies = await axios.get(`${API}/case-studies`);
        const related = allStudies.data.filter(s => s.slug !== slug).slice(0, 2);
        setRelatedStudies(related);
      } catch (error) {
        console.error("Error fetching case study:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchCaseStudy();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="text-[#00E676] animate-pulse">Loading...</div>
      </div>
    );
  }

  if (!caseStudy) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <PageMeta title="Case Study Not Found | Digital Media Ninja" robots="noindex, nofollow" />
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Case study not found</h1>
          <Button onClick={() => navigate("/case-studies")} className="btn-ninja">
            View All Case Studies
          </Button>
        </div>
      </div>
    );
  }

  const studyTitle = caseStudy.title;
  const clientName = caseStudy.client || caseStudy.client_name || "Client";
  const industry = caseStudy.industry;
  const location = caseStudy.location;
  const challenge = caseStudy.challenge;
  const solution = caseStudy.solution;
  const strategies = caseStudy.strategies || caseStudy.services || [];
  // Handle results as either array or object
  const resultsObj = caseStudy.results || {};
  const results = Array.isArray(resultsObj) ? resultsObj : Object.entries(resultsObj).map(([key, value]) => `${key.replace(/_/g, ' ')}: ${value}`);
  const metrics = caseStudy.metrics || (typeof caseStudy.results === 'object' && !Array.isArray(caseStudy.results) ? caseStudy.results : {});
  // Handle testimonial as either string or object
  const testimonial = typeof caseStudy.testimonial === 'object' ? caseStudy.testimonial?.quote : caseStudy.testimonial;
  const testimonialAuthor = typeof caseStudy.testimonial === 'object' 
    ? `${caseStudy.testimonial?.author || ''}${caseStudy.testimonial?.role ? ', ' + caseStudy.testimonial.role : ''}`
    : caseStudy.testimonial_author;
  const featuredImage = caseStudy.featured_image || caseStudy.image;

  // Case Study Schema for AEO/GEO
  const caseStudySchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": studyTitle,
    "description": challenge,
    "image": featuredImage,
    "author": {
      "@type": "Organization",
      "name": "Digital Media Ninja"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Digital Media Ninja"
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-20" data-testid="case-study-detail-page">
      <Helmet>
        <title>{`${studyTitle} | SEO Case Study | Digital Media Ninja`}</title>
        <meta name="description" content={`${clientName} case study: ${challenge?.substring(0, 140)}...`} />
        <link rel="canonical" href={`https://digitalmedianinja.com/case-studies/${slug}`} />
        <meta property="og:title" content={studyTitle} />
        <meta property="og:description" content={challenge} />
        <meta property="og:image" content={featuredImage || DEFAULT_IMAGE} />
        <meta property="og:url" content={`https://digitalmedianinja.com/case-studies/${slug}`} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={studyTitle} />
        <meta name="twitter:description" content={challenge} />
      </Helmet>

      {/* Schema Markup */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(caseStudySchema) }} />

      {/* Hero Section */}
      <div className="container-custom mb-16">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-8">
          <Link to="/" className="hover:text-[#00E676]">Home</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/case-studies" className="hover:text-[#00E676]">Case Studies</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-gray-400 truncate max-w-[200px]">{clientName}</span>
        </nav>

        <Button 
          variant="ghost" 
          className="mb-6 text-gray-400 hover:text-white"
          onClick={() => navigate("/case-studies")}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          All Case Studies
        </Button>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <div className="flex flex-wrap gap-3">
              <span className="px-4 py-2 rounded-full bg-[#00E676]/20 text-[#00E676] text-sm font-medium">
                {industry}
              </span>
              {location && (
                <span className="px-4 py-2 rounded-full bg-white/10 text-gray-300 text-sm flex items-center gap-1">
                  <MapPin className="w-4 h-4" /> {location}
                </span>
              )}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold">{studyTitle}</h1>
            <p className="text-xl text-gray-400">Client: {clientName}</p>
          </div>
          <div className="relative rounded-2xl overflow-hidden">
            <img 
              src={featuredImage} 
              alt={clientName}
              className="w-full h-80 object-cover"
              onError={(e) => {
                e.target.src = "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200";
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          </div>
        </div>
      </div>

      {/* Metrics Highlight */}
      <MetricsBar metrics={metrics} />

      {/* Challenge Section */}
      <div className="container-custom mb-16">
        <div className="max-w-4xl">
          <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
            <Target className="w-8 h-8 text-[#00E676]" />
            The Challenge
          </h2>
          <p className="text-lg text-gray-300 leading-relaxed">{challenge}</p>
        </div>
      </div>

      {/* Solution Section */}
      <div className="bg-[#0A0A0A] py-16 mb-16">
        <div className="container-custom">
          <div className="max-w-4xl">
            <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-[#00E676]" />
              Our Solution
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-8">{solution}</p>
            
            {strategies.length > 0 && (
              <div>
                <h3 className="text-xl font-bold mb-4">Key Strategies Implemented:</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {strategies.map((strategy, i) => (
                    <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-white/5">
                      <CheckCircle2 className="w-5 h-5 text-[#00E676] shrink-0 mt-1" />
                      <span className="text-gray-300">{strategy}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Results Section */}
      <div className="container-custom mb-16">
        <h2 className="text-3xl font-bold mb-8 flex items-center gap-3">
          <CheckCircle2 className="w-8 h-8 text-[#00E676]" />
          The Results
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          {results.map((result, i) => (
            <div key={i} className="flex items-start gap-4 p-6 rounded-xl bg-white/5 border border-white/10">
              <div className="w-10 h-10 rounded-full bg-[#00E676]/20 flex items-center justify-center shrink-0 text-[#00E676] font-bold">
                {i + 1}
              </div>
              <p className="text-lg text-gray-200">{result}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Testimonial */}
      {testimonial && (
        <div className="bg-gradient-to-r from-[#00E676]/10 to-[#6366F1]/10 py-16 mb-16">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto text-center">
              <Quote className="w-12 h-12 text-[#00E676] mx-auto mb-6" />
              <blockquote className="text-2xl font-medium text-white mb-6 italic">
                &ldquo;{testimonial}&rdquo;
              </blockquote>
              {testimonialAuthor && (
                <p className="text-gray-400">— {testimonialAuthor}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <div className="container-custom mb-16">
        <div className="glass rounded-2xl p-12 text-center">
          <h2 className="text-3xl font-bold mb-4">Want Results Like These?</h2>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            Let us analyze your business and create a custom strategy to achieve similar results for your {industry.toLowerCase()} business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="btn-ninja px-8">Get Your Free Audit</Button>
            </Link>
            <a href="tel:+14329349470">
              <Button className="btn-outline px-8">Call (432) 934-9470</Button>
            </a>
          </div>
        </div>
      </div>

      {/* Related Case Studies */}
      {relatedStudies.length > 0 && (
        <div className="container-custom">
          <h2 className="text-3xl font-bold mb-8">More Success Stories</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {relatedStudies.map((study) => (
              <RelatedStudyCard key={study.id} study={study} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Metrics Bar Component
const METRIC_WORDS = { ai: "AI", serps: "SERPs", est: "Est." };
const formatMetricLabel = (key) =>
  key.split("_").map((w) => METRIC_WORDS[w] || w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

const MetricsBar = ({ metrics }) => {
  const metricsList = Object.entries(metrics).slice(0, 4);
  
  if (metricsList.length === 0) return null;

  return (
    <div className="border-y border-white/10 py-12 mb-16">
      <div className="container-custom">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {metricsList.map(([key, value]) => (
            <div key={key} className="text-center">
              <p className="text-4xl font-bold text-[#00E676] mb-2">{value}</p>
              <p className="text-gray-500 text-sm">{formatMetricLabel(key)}</p>
            </div>
          ))}
        </div>
        <p className="text-center text-gray-500 text-xs mt-8" data-testid="case-metrics-source">
          Search metrics: Semrush US database snapshot (July 2026) · third-party estimates, not first-party Analytics. <Link to="/editorial-policy" className="underline hover:text-gray-400">How we verify claims</Link>
        </p>
      </div>
    </div>
  );
};

// Related Study Card
const RelatedStudyCard = ({ study }) => (
  <Card className="bg-[#0A0A0A] border-white/10 hover:border-[#00E676]/30 transition-all overflow-hidden group">
    <div className="relative h-48">
      <img 
        src={study.featured_image || study.image}
        alt={study.title}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        onError={(e) => {
          e.target.src = "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800";
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] to-transparent" />
      <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-[#00E676]/20 text-[#00E676] text-sm">
        {study.industry}
      </span>
    </div>
    <CardContent className="p-6">
      <h3 className="text-xl font-bold mb-3 group-hover:text-[#00E676] transition-colors">
        {study.title}
      </h3>
      <p className="text-gray-400 text-sm mb-4 line-clamp-2">
        {study.challenge}
      </p>
      <Link to={"/case-studies/" + study.slug}>
        <Button variant="ghost" className="text-[#00E676] hover:text-[#00C853] p-0">
          Read Case Study <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </Link>
    </CardContent>
  </Card>
);

export default CaseStudyDetailPage;
